import type { AppError } from '../../api/errors';
import type {
  AutonomyKind,
  Employee,
  EmployeeModule,
  EmployeeProfile,
  EmployeeRequestInput,
  ModuleCategory,
  ModuleQuestion,
  Workspace,
} from '../../domain/models';
import type { Translate } from '../../l10n/i18n';

// What asking for a new employee needs besides the screens (ADR 0010): the shelf by category, where a
// role already works, the form's state, its checks, and the server's field errors on its fields.

export const categoryOrder: readonly ModuleCategory[] = ['customers', 'marketing', 'sales', 'admin', 'tech'];

export function categoryName(t: Translate, c: ModuleCategory): string {
  switch (c) {
    case 'customers':
      return t('hireCategoryCustomers');
    case 'marketing':
      return t('hireCategoryMarketing');
    case 'sales':
      return t('hireCategorySales');
    case 'admin':
      return t('hireCategoryAdmin');
    case 'tech':
      return t('hireCategoryTech');
  }
}

/** The shelf by category, each in the shelf's own order; empty categories are left out. */
export const byCategory = (modules: EmployeeModule[]) =>
  categoryOrder
    .map((category) => ({ category, modules: modules.filter((m) => m.category === category) }))
    .filter((g) => g.modules.length > 0);

/** The projects where this role already works, in the workspaces' order. */
export const worksIn = (role: string, employees: Employee[], workspaces: Workspace[]): Workspace[] =>
  workspaces.filter((w) => employees.some((e) => e.role === role && e.workspaceId === w.id));

/** The workspace choice for a project that does not exist yet. */
export const NEW_WORKSPACE = 'new';

/** A description the Talkos team can work from: a few sentences, as the server asks (ADR 0010). */
export const minDescription = 30;
const described = (s: string) => [...s.trim()].length >= minDescription;

/** What the hiring form holds. `workspace` is a workspace id or `NEW_WORKSPACE`. */
export interface HireDraft {
  workspace: string;
  newWorkspace: string;
  /** What the new project is. */
  newWorkspaceAbout: string;
  answers: Record<string, string>;
  connections: string[];
  automatic: AutonomyKind[];
  /** For a module: anything else that matters. For an employee of one's own: what it should do. */
  note: string;
}

/** Answers to start the form with, handed over by whoever opened it (the demo's tour). */
export interface HirePrefill {
  answers?: Record<string, string>;
  note?: string;
}

/** A fresh form: the project the address asks for (by slug or id) unless the role already works
 * there, else the first one where it does not, else a new one; every connection on; nothing automatic;
 * answers as `prefill` gives them. */
export function initialDraft(
  module: EmployeeModule | undefined,
  workspaces: Workspace[],
  taken: Workspace[],
  preferred?: string | null,
  prefill?: HirePrefill,
): HireDraft {
  const free = workspaces.filter((w) => !taken.some((x) => x.id === w.id));
  const wanted = free.find((w) => w.slug === preferred || w.id === preferred);
  return {
    workspace: (wanted ?? free[0])?.id ?? NEW_WORKSPACE,
    newWorkspace: '',
    newWorkspaceAbout: '',
    answers: { ...prefill?.answers },
    connections: module?.connections.map((c) => c.key) ?? [],
    automatic: [],
    note: prefill?.note ?? '',
  };
}

/** Field ids for errors, shared by the form and the server's answer. */
export const fieldId = {
  answer: (key: string) => `answers.${key}`,
  workspace: 'workspace',
  workspaceAbout: 'workspaceAbout',
  /** An employee of one's own: the description of its work. */
  description: 'description',
  automatic: 'automatic',
};

const blank = (s: string | undefined) => (s ?? '').trim() === '';

/** Required answers that are missing, as field errors. */
export function missingAnswers(t: Translate, questions: ModuleQuestion[], answers: Record<string, string>): Record<string, string> {
  return Object.fromEntries(questions.filter((q) => q.required && blank(answers[q.key])).map((q) => [fieldId.answer(q.key), t('hireRequired')]));
}

/** A required description: missing, or too short to work from. */
const descriptionError = (t: Translate, s: string) => (blank(s) ? t('hireRequired') : described(s) ? undefined : t('hireDescribeMore'));

/** What to fix before sending: required answers, a new project's name and description, the description
 * of an employee of one's own. */
export function validateHire(t: Translate, module: EmployeeModule | undefined, d: HireDraft): Record<string, string> {
  const errors = module ? missingAnswers(t, module.questions, d.answers) : {};
  if (!module) {
    const e = descriptionError(t, d.note);
    if (e) errors[fieldId.description] = e;
  }
  if (d.workspace === NEW_WORKSPACE) {
    if (blank(d.newWorkspace)) errors[fieldId.workspace] = t('hireRequired');
    const e = descriptionError(t, d.newWorkspaceAbout);
    if (e) errors[fieldId.workspaceAbout] = e;
  }
  return errors;
}

/** Answers without the empty ones, trimmed. */
export function cleanAnswers(answers: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(answers).flatMap(([k, v]) => (blank(v) ? [] : [[k, v.trim()]])));
}

/** The request the form sends. Only the module's own connections and automatable kinds go along. An
 * employee of one's own is its description (`profile.note`); the server names it from that. */
export function hireInput(module: EmployeeModule | undefined, d: HireDraft): EmployeeRequestInput {
  const isNew = d.workspace === NEW_WORKSPACE;
  const profile: EmployeeProfile = {
    answers: module ? cleanAnswers(d.answers) : {},
    connections: module ? module.connections.map((c) => c.key).filter((k) => d.connections.includes(k)) : [],
    note: blank(d.note) ? undefined : d.note.trim(),
  };
  return {
    role: module?.role,
    workspaceId: isNew ? undefined : d.workspace,
    workspaceName: isNew ? d.newWorkspace.trim() : undefined,
    workspaceDescription: isNew ? d.newWorkspaceAbout.trim() : undefined,
    profile,
    automaticKinds: module ? module.automatable.filter((k) => d.automatic.includes(k)) : [],
  };
}

/** Field errors without one field's, once the person changed it. */
export function without(errors: Record<string, string>, field: string): Record<string, string> {
  const rest = { ...errors };
  delete rest[field];
  return rest;
}

/** A server's word on a field in the app's words: a missing value, a description too short, or
 * something to check. */
function fieldMessage(t: Translate, message: string): string {
  if (message.startsWith('required')) return t('hireRequired');
  if (message.startsWith('at least')) return t('hireDescribeMore');
  return t('errorValidation');
}

/** The server's field errors on the form's fields (`profile.answers.<key>`, `answers.<key>`,
 * `profile.note`, `workspace_id`, `workspace_name`, `workspace_description`, `title`, `automatic_kinds`).
 * `placed` says whether any landed on a field the form shows. */
export function serverFieldErrors(t: Translate, e: AppError, shown: (id: string) => boolean): { fields: Record<string, string>; placed: boolean } {
  const fields: Record<string, string> = {};
  for (const [field, message] of Object.entries(e.fields)) {
    const answer = /^(?:profile\.)?answers\.(.+)$/.exec(field)?.[1];
    const id =
      answer !== undefined
        ? fieldId.answer(answer)
        : field === 'workspace_id' || field === 'workspace_name'
          ? fieldId.workspace
          : field === 'workspace_description'
            ? fieldId.workspaceAbout
            : field === 'profile.note' || field === 'title'
              ? fieldId.description
              : field === 'automatic_kinds'
                ? fieldId.automatic
                : undefined;
    if (id && shown(id)) fields[id] ??= fieldMessage(t, message);
  }
  return { fields, placed: Object.keys(fields).length > 0 };
}
